/**
 * Super Admin section access (keep in sync with server-api/helpers/saAccess.js).
 * Levels: none | read | write
 * Clients also: login (read + login-as-client), ranked between read and write
 * Client scope: allowedClientIds — null = all; [] = none; [ids] = those only
 */

export const SA_ACCESS_LEVELS = [
    { value: 'none', label: 'No access' },
    { value: 'read', label: 'Read only' },
    { value: 'write', label: 'Read & write' },
]

/** Clients section levels (includes login-as). */
export const CLIENTS_ACCESS_LEVELS = [
    { value: 'none', label: 'No access' },
    { value: 'read', label: 'Read only' },
    {
        value: 'login',
        label: 'Read & login',
        helperText: 'View clients plus login as the club admin',
    },
    { value: 'write', label: 'Read & write' },
]

export const SA_SECTIONS = [
    {
        key: 'clients',
        label: 'Clients',
        description:
            'Client list, create/edit, bulk import; login = view + login-as (limited to selected clients)',
        pathPrefixes: [
            '/pages/client-list',
            '/pages/new-client',
            '/pages/view-client',
            '/clients/bulk-import',
            '/pages/client-login',
        ],
        writePathPrefixes: [
            '/pages/new-client',
            '/pages/view-client',
            '/clients/bulk-import',
        ],
        loginPathPrefixes: ['/pages/client-login'],
    },
    {
        key: 'jobs',
        label: 'Jobs',
        description: 'New jobs, in production, dispatched',
        // Lists are readable; mutations are enforced by API + page UI
        pathPrefixes: ['/pages/jobs/'],
        writePathPrefixes: [],
        loginPathPrefixes: [],
    },
    {
        key: 'settings',
        label: 'Settings',
        description: 'Account settings and related admin tools',
        // Personal password page is always allowed; other settings paths gated
        pathPrefixes: ['/settings/email-logs'],
        writePathPrefixes: [],
        loginPathPrefixes: [],
    },
]

export const SA_SECTION_KEYS = SA_SECTIONS.map((s) => s.key)

const LEVEL_RANK = { none: 0, read: 1, login: 2, write: 3 }

export function accessLevelsForSection(sectionKey) {
    return sectionKey === 'clients' ? CLIENTS_ACCESS_LEVELS : SA_ACCESS_LEVELS
}

export function normalizeAccessLevel(value, { allowLogin = false } = {}) {
    const raw = String(value || '')
        .trim()
        .toLowerCase()
    if (
        raw === 'rw' ||
        raw === 'readwrite' ||
        raw === 'read_write' ||
        raw === 'full'
    ) {
        return 'write'
    }
    if (
        raw === 'ro' ||
        raw === 'readonly' ||
        raw === 'read_only' ||
        raw === 'view'
    ) {
        return 'read'
    }
    if (raw === 'login' || raw === 'login_as' || raw === 'loginas') {
        return allowLogin ? 'login' : 'read'
    }
    const allowed = allowLogin
        ? ['none', 'read', 'login', 'write']
        : ['none', 'read', 'write']
    if (allowed.includes(raw)) return raw
    return 'none'
}

export function isMasterSa(user) {
    return Array.isArray(user?.permission) && user.permission.includes('master')
}

export function fullWriteAccess() {
    return SA_SECTION_KEYS.reduce((acc, key) => {
        acc[key] = 'write'
        return acc
    }, {})
}

function hasExplicitSectionAccess(raw) {
    return raw && typeof raw === 'object' && Object.keys(raw).length > 0
}

export function normalizeSectionAccess(raw, { isMaster = false } = {}) {
    if (isMaster) return fullWriteAccess()
    const src = raw && typeof raw === 'object' ? raw : {}
    const out = {}
    for (const key of SA_SECTION_KEYS) {
        out[key] = normalizeAccessLevel(src[key], {
            allowLogin: key === 'clients',
        })
    }
    return out
}

export function resolveSaSectionAccess(user) {
    if (!user || user.role !== 'SA') {
        return normalizeSectionAccess({}, { isMaster: false })
    }
    if (isMasterSa(user)) return fullWriteAccess()

    const raw = user.sectionAccess
    if (!hasExplicitSectionAccess(raw)) {
        // Legacy secondary SA with no ACL configured — keep previous full access
        return fullWriteAccess()
    }
    return normalizeSectionAccess(raw, { isMaster: false })
}

export function getSaSectionAccess(user, sectionKey) {
    return resolveSaSectionAccess(user)[sectionKey] || 'none'
}

export function hasSaSectionAccess(user, sectionKey, minLevel = 'read') {
    const needed = normalizeAccessLevel(minLevel, {
        allowLogin: sectionKey === 'clients' || minLevel === 'login',
    })
    const actual = getSaSectionAccess(user, sectionKey)
    return (LEVEL_RANK[actual] || 0) >= (LEVEL_RANK[needed] || 0)
}

export function canReadSaSection(user, sectionKey) {
    return hasSaSectionAccess(user, sectionKey, 'read')
}

export function canWriteSaSection(user, sectionKey) {
    return hasSaSectionAccess(user, sectionKey, 'write')
}

export function canLoginAsClientSa(user) {
    return hasSaSectionAccess(user, 'clients', 'login')
}

export function toClientIdString(value) {
    if (value == null) return null
    if (typeof value === 'object' && value._id != null) return String(value._id)
    const s = String(value).trim()
    return s || null
}

export function normalizeClientIdList(raw) {
    if (!Array.isArray(raw)) return []
    const seen = new Set()
    const out = []
    for (const item of raw) {
        const id = toClientIdString(item)
        if (!id || seen.has(id)) continue
        seen.add(id)
        out.push(id)
    }
    return out
}

/**
 * @returns {null|string[]} null = all clients; string[] = only those (may be empty)
 */
export function resolveAllowedClientIds(user) {
    if (!user || user.role !== 'SA') return null
    if (isMasterSa(user)) return null
    if (!hasExplicitSectionAccess(user.sectionAccess)) return null
    if (!hasSaSectionAccess(user, 'clients', 'read')) return []

    // API/JWT may already send resolved null for unrestricted
    if (user.allowedClientIds === null) return null
    return normalizeClientIdList(user.allowedClientIds)
}

export function isSaClientUnrestricted(user) {
    return resolveAllowedClientIds(user) === null
}

export function canAccessClient(user, clientId) {
    if (!user || user.role !== 'SA') return true
    if (!hasSaSectionAccess(user, 'clients', 'read')) return false
    const allowed = resolveAllowedClientIds(user)
    if (allowed === null) return true
    const id = toClientIdString(clientId)
    if (!id) return false
    return allowed.includes(id)
}

/** Resolve which SA section a pathname belongs to (longest prefix wins). */
export function sectionForPath(pathname) {
    const path = String(pathname || '')
    let best = null
    let bestLen = -1
    for (const section of SA_SECTIONS) {
        for (const prefix of section.pathPrefixes) {
            if (path === prefix || path.startsWith(prefix)) {
                if (prefix.length > bestLen) {
                    best = section.key
                    bestLen = prefix.length
                }
            }
        }
    }
    return best
}

/** Extract a client/club id from common SA client routes. */
export function clientIdFromPath(pathname) {
    const path = String(pathname || '')
    const patterns = [
        /^\/pages\/new-client\/([^/]+)/,
        /^\/pages\/view-client\/([^/]+)/,
        /^\/pages\/client-login\/([^/]+)/,
        /^\/clients\/bulk-import\/([^/]+)/,
    ]
    for (const re of patterns) {
        const m = path.match(re)
        if (m?.[1] && m[1] !== 'new') return m[1]
    }
    return null
}

/** True when this path is a write/mutating surface for its section. */
export function pathRequiresWrite(pathname) {
    const path = String(pathname || '')
    const sectionKey = sectionForPath(path)
    if (!sectionKey) return false
    const section = SA_SECTIONS.find((s) => s.key === sectionKey)
    if (!section?.writePathPrefixes?.length) return false
    return section.writePathPrefixes.some(
        (prefix) => path === prefix || path.startsWith(prefix)
    )
}

/** True when this path needs login-as (clients) access. */
export function pathRequiresLogin(pathname) {
    const path = String(pathname || '')
    const sectionKey = sectionForPath(path)
    if (!sectionKey) return false
    const section = SA_SECTIONS.find((s) => s.key === sectionKey)
    if (!section?.loginPathPrefixes?.length) return false
    return section.loginPathPrefixes.some(
        (prefix) => path === prefix || path.startsWith(prefix)
    )
}

/** Minimum section level required for a path. */
export function minLevelForPath(pathname) {
    if (pathRequiresWrite(pathname)) return 'write'
    if (pathRequiresLogin(pathname)) return 'login'
    return 'read'
}

export function defaultSectionAccessForSecondary() {
    return {
        clients: 'write',
        jobs: 'write',
        settings: 'read',
    }
}
